/**
 * Should this company be called at all.
 *
 * At the TOP of the card, above the tabs, and that placement is the argument:
 * docs/muugijuhis.md §6 says a salesperson who cannot say no burns a month on
 * one deal, and a verdict buried behind a tab is a verdict nobody reads until
 * after they have already dialled.
 *
 * IT WARNS, IT DOES NOT LOCK. The operator knows things the register does not -
 * that the owner is a neighbour, that the company is about to rebrand - and a
 * tool that refuses to show the arguments because a model said "no" would just
 * be worked around. So a "no" is loud and everything below it still works.
 */
import { CircleSlash, Sparkles, ThumbsUp, TriangleAlert } from "lucide-react";
import type { BrainSynthesis } from "../../../../lib/siteBrain";
import { StatusPill } from "../../../ui";
import type { T } from "../../site-scanner/types";

/** The theme's own verdict colours, the same three the severity map uses. */
const TONE: Record<BrainSynthesis["fit"]["decision"], { colour: string; icon: typeof ThumbsUp }> = {
  go: { colour: "var(--color-success)", icon: ThumbsUp },
  hold: { colour: "var(--color-warning)", icon: TriangleAlert },
  no: { colour: "var(--color-destructive)", icon: CircleSlash },
};

export function BrainFit({ synthesis, t }: { synthesis: BrainSynthesis; t: T }) {
  const { fit } = synthesis;
  const tone = TONE[fit.decision];
  const Icon = tone.icon;

  return (
    <section
      className="space-y-2 rounded-2xl bg-[var(--crm-sunk)] px-4 py-3"
      style={{ boxShadow: `inset 3px 0 0 0 color-mix(in oklab, ${tone.colour} 70%, transparent)` }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="size-4 shrink-0" style={{ color: tone.colour }} />
        <span className="text-sm font-semibold">{t(`brain.fit.decision.${fit.decision}`)}</span>

        {fit.segment ? <StatusPill tone="mut">{t(`brain.fit.segment.${fit.segment}`)}</StatusPill> : null}

        {/* The 40-minute method (muugijuhis §7.1) is worth it only on the best
            targets, so the flag has to be visible before the work starts. */}
        {fit.demoCandidate ? (
          <StatusPill tone="ok">
            <Sparkles className="size-3" /> {t("brain.fit.demoCandidate")}
          </StatusPill>
        ) : null}
      </div>

      {fit.reasons.length ? (
        <ul className="space-y-0.5 text-sm leading-snug">
          {fit.reasons.map((reason) => (
            <li key={reason} className="flex gap-2">
              <span aria-hidden className="text-muted-foreground">
                -
              </span>
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {fit.stopFlags.length ? (
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span>{t("brain.fit.stopFlags")}</span>
          {fit.stopFlags.map((flag) => (
            <StatusPill key={flag} tone="wt">
              {t(`brain.stopFlag.${flag}`)}
            </StatusPill>
          ))}
        </p>
      ) : null}
    </section>
  );
}
